import { Radio, Star } from "lucide-react";
import ServeBox from "@/components/ServeBox";
import CourtTimer from "@/components/CourtTimer";

function StateBadge({ state, game }) {
  const cls = state === "live" ? "border-red-500 text-red-400" : state === "final" ? "border-[#A3E635] text-[#A3E635]" : "border-zinc-700 text-zinc-400";
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 border ${cls}`}>
      {state === "live" && <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />}
      {state === "live" ? `Live · G${game || 1}` : state === "final" ? "Final" : "Up Next"}
    </span>
  );
}

function Row({ name, score, winner, state, serving, testid }) {
  return (
    <ServeBox serving={serving && state !== "final"} testid={testid} className={`flex items-center justify-between gap-2 ${winner ? "text-[#A3E635]" : state === "final" ? "text-zinc-500" : "text-white"}`}>
      <span className="text-xs font-bold truncate">{name || "TBD"}</span>
      <span className="font-mono font-black tabular-nums text-2xl leading-none">{state === "upcoming" ? "–" : score ?? 0}</span>
    </ServeBox>
  );
}

export function CourtTile({ m }) {
  const id = `court-tile-${m.tid}-${m.key}`;
  return (
    <div data-testid={id} className={`border p-3 bg-zinc-950 transition-colors ${m.main_court ? "border-red-500/60" : m.state === "live" ? "border-zinc-600" : "border-zinc-800"}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-display font-black uppercase text-sm text-[#22D3EE]" data-testid={`${id}-court`}>Court {m.court}</span>
          {m.main_court && <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-red-400" data-testid={`${id}-main`}><Star className="w-3 h-3 fill-red-400" /> Main</span>}
        </div>
        <span className="flex items-center gap-1.5">
          {m.state !== "final" && <CourtTimer since={m.court_assigned_at} testid={`${id}-timer`} />}
          <StateBadge state={m.state} game={m.game} />
        </span>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 truncate mb-2" data-testid={`${id}-label`}>{m.label}{m.round ? ` · ${typeof m.round === "number" ? `Round ${m.round}` : m.round}` : ""}</div>
      <div className="space-y-2.5 pt-1">
        <Row name={m.side_a} score={m.score_a} winner={m.winner === "a"} state={m.state} serving={m.first_serve === "a"} testid={`${id}-a`} />
        <Row name={m.side_b} score={m.score_b} winner={m.winner === "b"} state={m.state} serving={m.first_serve === "b"} testid={`${id}-b`} />
      </div>
      {m.games?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1" data-testid={`${id}-games`}>
          {m.games.map((g, i) => <span key={i} className="font-mono text-[10px] border border-zinc-800 px-1.5 py-0.5 text-zinc-400">{g[0]}–{g[1]}</span>)}
        </div>
      )}
    </div>
  );
}

export function LiveCourtsStrip({ courts }) {
  if (!courts?.length) {
    return (
      <div className="border border-zinc-800 bg-zinc-950 p-6 text-center text-zinc-500" data-testid="live-courts-empty">
        <Radio className="w-6 h-6 mx-auto mb-2 text-zinc-700" />
        <div className="font-display uppercase tracking-widest text-sm">No courts in play</div>
        <div className="text-xs mt-1">Every active court shows up here once a tournament is in progress.</div>
      </div>
    );
  }
  const groups = [];
  for (const m of courts) {
    let g = groups.find((x) => x.tid === m.tid);
    if (!g) { g = { tid: m.tid, name: m.tournament, rows: [] }; groups.push(g); }
    g.rows.push(m);
  }
  const liveCount = courts.filter((c) => c.state === "live").length;
  return (
    <div data-testid="live-courts-strip">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="font-display font-black uppercase text-lg text-white">Live Courts</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500" data-testid="live-courts-count">{liveCount} live · {courts.length} on court</div>
      </div>
      <div className="space-y-4">
        {groups.map((g) => (
          <div key={g.tid} data-testid={`live-courts-group-${g.tid}`}>
            {groups.length > 1 && <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2 border-b border-zinc-800 pb-1 truncate" data-testid={`live-courts-group-${g.tid}-name`}>{g.name}</div>}
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {g.rows.map((m) => <CourtTile key={`${m.tid}-${m.key}`} m={m} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
